import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { TransitTaskFormDto, type TransitTaskFormInterface } from '@zaoblako/shared/modules/cctv/dtos/transit-task/transit-task-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { TransitTaskService } from '@zaoblako/web/core/services/passage/transit-task.service';
import { haveRoomIdsChanged } from '@zaoblako/web/modules/passage/utils/passage-form-room-category-filters.util';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { CarCategorySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/car-category-single-select/car-category-single-select.component';
import { ExternalUserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-single-select/external-user-single-select.component';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import { RoomMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-multi-select/room-multi-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { TransitTaskFindInterface } from '@zaoblako/shared/modules/cctv/dtos/transit-task/transit-task-find.dto';

import { isNil } from 'lodash';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { MessagesModule } from 'primeng/messages';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'crm-transit-task-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    SpinnerComponent,
    ButtonComponent,
    JoinPipe,
    DatepickerComponent,
    InputTextComponent,
    DividerModule,
    ExternalUserSingleSelectComponent,
    UserGroupMultiSelectComponent,
    UserMultiSelectComponent,
    UserSingleSelectComponent,
    CarCategorySingleSelectComponent,
    ClientAutoCompleteComponent,
    RoomMultiSelectComponent,
    MessagesModule
  ],
  templateUrl: './transit-task-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TransitTaskFormComponent extends BaseFormComponent<TransitTaskFormInterface> implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public task = <TransitTaskFormInterface>{};
  public dto = TransitTaskFormDto;

  public selectedRoomsIds: number[] = [];

  private roomsBeforeLastChange: RoomFindInterface[] = [];

  @ViewChild('externalUserSingleSelectComponent')
  public readonly externalUserSingleSelectComponent: ExternalUserSingleSelectComponent;

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  private formSubscription$: Subscription = new Subscription();

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly transitTaskService: TransitTaskService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy(): void {
    this.formSubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.transitTaskService
      .findById<TransitTaskFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (task) => {
          this.task = {
            ...task,
            rooms: task.rooms ?? []
          };
          this.rememberRoomsSnapshot(this.task.rooms);

          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTask() {
    if (this.checkValidate(this.task).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.transitTaskService.update<TransitTaskFormInterface, TransitTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Заявка №${task?.id} обновлена`);
            this.formStoreService.saveNext(task, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.transitTaskService.create<TransitTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Заявка создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/passage/transit-task/card/' + task.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteTask() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить заявку №${this.formStoreService.getState.forms[this.index]?.entityId}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.transitTaskService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(
            `Заявка №${this.formStoreService.getState.forms[this.index]?.entityId} удалена`
          );
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  /** Same as card v2 requisites: `dependsOn.categoryIdsPath` → `category.id`. */
  public get roomCategoryIdsFilter(): number[] {
    const categoryId = this.task?.category?.id;
    if (isNil(categoryId)) {
      return [];
    }
    return [categoryId];
  }

  public selectedClient() {
    this.task.externalUser = null;
    this.task.rooms = [];
    this.task.category = null;
    this.roomsBeforeLastChange = [];
    this.selectedRoomsIds = [];
    this.cdRef.markForCheck();
  }

  public onRoomsChange(rooms: RoomFindInterface[]): void {
    if (haveRoomIdsChanged(this.roomsBeforeLastChange, rooms)) {
      this.task.category = null;
    }
    this.rememberRoomsSnapshot(rooms);
    this.cdRef.markForCheck();
  }

  private rememberRoomsSnapshot(rooms?: RoomFindInterface[]): void {
    this.roomsBeforeLastChange = rooms?.length ? [...rooms] : [];
    this.selectedRoomsIds = rooms?.length
      ? rooms.map((room) => room.id)
      : [];
  }

  public openFormCreateExternalUser() {
    const result = this.formStoreService.addForm(TypeFormEnum.EXTERNAL_USER_CREATE, {
      headerText: 'Добавление внешнего пользователя',
      widthSize: 'form-sidebar-small',
      data: {
        client: this.task?.client
      }
    });

    this.formSubscription$.add(
      result.onSave.subscribe((externalUser: ExternalUserFindInterface) => {
        if (externalUser) {
          this.externalUserSingleSelectComponent?.firstLoad();
          this.task.externalUser = externalUser;
          this.cdRef.markForCheck();
        }
      })
    );
  }
}
